import { makeAutoObservable } from 'mobx'
import { ViewColumnFragment } from 'types/graphql'

import { TableViewColumn } from 'src/models/TableViews/TableViewColumn'
import { TableViewColumnTemplateStore } from 'src/models/TableViews/TableViewColumnTemplateStore'
import { filterNil } from 'src/utils'

export class ColumnManager {
  private _columns: TableViewColumn[] = []
  private _hiddenOverrides = new Map<string, string>()

  private _columnTemplateStore: TableViewColumnTemplateStore
  constructor(args: {
    columns: ViewColumnFragment[]
    columnTemplateStore: TableViewColumnTemplateStore
  }) {
    this._columnTemplateStore = args.columnTemplateStore
    const columns: TableViewColumn[] = []
    args.columns.forEach((c, displayIndex) => {
      columns.push(
        new TableViewColumn({
          columnGeneratedId: c.columnTemplateGeneratedId,
          columnTemplateStore: args.columnTemplateStore,
          displayIndex,
          displayLabelOverride: c.displayLabelOverride ?? null,
        })
      )
    })
    this._columns = columns
    makeAutoObservable(this)
  }

  public get columns(): TableViewColumn[] {
    // Columns with no column template should only include custom columns that were deleted in this session
    return this._columns.filter((c) => !!c.columnTemplate)
  }

  public set columns(value: TableViewColumn[]) {
    this._columns = value
  }

  public get columnsByColumnGeneratedId(): Map<string, TableViewColumn> {
    return new Map(
      filterNil(
        this.columns.map((c) => (c.columnTemplate ? [c.columnTemplate.generatedId, c] : null))
      )
    )
  }

  /**
   * Sets the per-column display-label override on the matching column,
   * or clears it back to the template-derived default when `label` is
   * null. Whitespace-only labels are coerced to null so the rendered
   * header falls through to `columnTemplate.labelTitleCase` (matching
   * the BE's `resolveColumnDisplayLabel` fallback chain — see
   * `displayLabel.ts`).
   */
  public setColumnDisplayLabelOverride(columnGeneratedId: string, label: string | null) {
    const column = this._columns.find((c) => c.columnTemplate?.generatedId === columnGeneratedId)
    if (!column) {
      return
    }
    const trimmed = label?.trim()
    column.setDisplayLabelOverride(trimmed ? trimmed : null)
  }

  public get hiddenOverrides(): ReadonlyMap<string, string> {
    return this._hiddenOverrides
  }

  public toggleColumnTypeVisibility(columnGeneratedId: string) {
    const index = this.columns.findIndex((c) => c.columnTemplate?.generatedId === columnGeneratedId)
    if (index < 0) {
      const columnTemplate = this._columnTemplateStore.columnTemplates.find(
        (ct) => ct.generatedId === columnGeneratedId
      )
      if (!columnTemplate) {
        return
      }
      const stashedOverride = this._hiddenOverrides.get(columnGeneratedId) ?? null
      this._hiddenOverrides.delete(columnGeneratedId)
      this._columns.push(
        new TableViewColumn({
          columnGeneratedId,
          columnTemplateStore: this._columnTemplateStore,
          displayIndex: this.columns.length,
          displayLabelOverride: stashedOverride,
        })
      )
    }
    // don't allow hiding last column
    else if (this.columns.length > 1) {
      const column = this.columns[index]
      if (column?.displayLabelOverride) {
        this._hiddenOverrides.set(columnGeneratedId, column.displayLabelOverride)
      } else {
        this._hiddenOverrides.delete(columnGeneratedId)
      }
      this._columns.splice(index, 1)
    }
  }
}
